import {
  ACTOR_KIND,
  EVENT_KIND,
  OWNER_KIND,
  createLedgerState,
  type LedgerEvent,
  type LedgerState,
  type OwnerRef,
  type Receipt,
} from "./ledger.ts";

export interface TendLineagePair {
  readonly cornItemId: number;
  readonly eggItemId: number;
  readonly lineageId: number;
}

export interface TendBatchCommand {
  readonly actorId: number;
  readonly actionId: number;
  readonly workOrderId: number;
  readonly stagedOwner: OwnerRef;
  readonly outputOwner: OwnerRef;
  readonly cornItemIds: readonly number[];
  readonly eggItemIds: readonly number[];
  readonly idempotencyKey: number;
  readonly cancelled: boolean;
}

export interface SaleBatchCommand {
  readonly actorId: number;
  readonly actionId: number;
  readonly workOrderId: number;
  readonly cargoOwner: OwnerRef;
  readonly marketOwner: OwnerRef;
  readonly eggItemIds: readonly number[];
  readonly cashPerEgg: number;
  readonly idempotencyKey: number;
  readonly cancelled: boolean;
}

export interface ManualChainTransactionResult {
  readonly committed: boolean;
  readonly reason: string;
  readonly state: LedgerState;
  readonly lineage: readonly TendLineagePair[];
  readonly cashDelta: number;
}

function integer(value: unknown, label: string, minimum = 0): number {
  if (Object.is(value, -0) || !Number.isSafeInteger(value) || (value as number) < minimum) {
    throw new RangeError(`${label} must be a safe integer >= ${minimum}`);
  }
  return value as number;
}

function ownerKey(owner: Pick<OwnerRef, "ownerKind" | "ownerId">): string {
  return `${owner.ownerKind}:${owner.ownerId}`;
}

function owner(state: LedgerState, ref: OwnerRef) {
  return state.owners.find(
    (candidate) => candidate.ownerKind === ref.ownerKind && candidate.ownerId === ref.ownerId,
  );
}

function actorReason(state: LedgerState, actorId: number, actionId: number): string | null {
  const actor = state.actors.find((candidate) => candidate.actorId === actorId);
  if (actor === undefined) return "actor-missing";
  if (actor.actorKind !== ACTOR_KIND.HUMANOID) return "actor-not-humanoid";
  if (!actor.enabled) return "actor-disabled";
  if (!actor.present) return "actor-not-present";
  if (actor.actionId !== actionId) return "action-mismatch";
  return null;
}

function rejected(state: LedgerState, reason: string): ManualChainTransactionResult {
  return Object.freeze({
    committed: false,
    reason,
    state,
    lineage: Object.freeze([]),
    cashDelta: 0,
  });
}

function nextVersion(value: number): number | null {
  return value === Number.MAX_SAFE_INTEGER ? null : value + 1;
}

function appendReceiptAndEvent(
  state: LedgerState,
  command: Pick<TendBatchCommand, "actorId" | "actionId" | "idempotencyKey">,
  eventKind: (typeof EVENT_KIND)[keyof typeof EVENT_KIND],
  reason: string,
  itemIds: readonly number[],
): Readonly<{ receipts: readonly Receipt[]; events: readonly LedgerEvent[]; nextEventSequence: number }> {
  if (state.nextEventSequence === Number.MAX_SAFE_INTEGER) {
    throw new RangeError("event sequence is exhausted");
  }
  const sequence = state.nextEventSequence;
  const receipt: Receipt = Object.freeze({ sequence, idempotencyKey: command.idempotencyKey });
  const event: LedgerEvent = Object.freeze({
    sequence,
    eventKind,
    reason,
    actorId: command.actorId,
    actionId: command.actionId,
    idempotencyKey: command.idempotencyKey,
    itemIds: Object.freeze([...itemIds].sort((left, right) => left - right)),
  });
  return Object.freeze({
    receipts: Object.freeze([...state.receipts, receipt]),
    events: Object.freeze([...state.events, event]),
    nextEventSequence: sequence + 1,
  });
}

function validateCommon(
  state: LedgerState,
  command: Pick<TendBatchCommand, "actorId" | "actionId" | "workOrderId" | "idempotencyKey" | "cancelled">,
): string | null {
  try {
    integer(command.actorId, "actorId", 1);
    integer(command.actionId, "actionId", 1);
    integer(command.workOrderId, "workOrderId", 1);
    integer(command.idempotencyKey, "idempotencyKey", 1);
  } catch {
    return "invalid-command";
  }
  if (command.cancelled) return "cancelled";
  const invalidActor = actorReason(state, command.actorId, command.actionId);
  if (invalidActor !== null) return invalidActor;
  if (state.receipts.some((receipt) => receipt.idempotencyKey === command.idempotencyKey)) {
    return "duplicate-idempotency-key";
  }
  return null;
}

export function commitAttendedTendBatch(
  state: LedgerState,
  command: TendBatchCommand,
): ManualChainTransactionResult {
  const commonReason = validateCommon(state, command);
  if (commonReason !== null) return rejected(state, commonReason);
  const cornItemIds = [...command.cornItemIds].sort((left, right) => left - right);
  const eggItemIds = [...command.eggItemIds].sort((left, right) => left - right);
  if (
    cornItemIds.length === 0 ||
    cornItemIds.length !== eggItemIds.length ||
    new Set(cornItemIds).size !== cornItemIds.length ||
    new Set(eggItemIds).size !== eggItemIds.length ||
    eggItemIds.some((itemId) => state.items.some((item) => item.itemId === itemId))
  ) return rejected(state, "invalid-batch-items");

  const stagedOwner = owner(state, command.stagedOwner);
  const outputOwner = owner(state, command.outputOwner);
  if (stagedOwner === undefined || outputOwner === undefined) return rejected(state, "owner-missing");
  if (stagedOwner.ownerKind !== OWNER_KIND.PEN_STAGING || outputOwner.ownerKind !== OWNER_KIND.PEN_OUTPUT) {
    return rejected(state, "wrong-owner-kind");
  }
  if (stagedOwner.version !== command.stagedOwner.version) return rejected(state, "stale-source-version");
  if (outputOwner.version !== command.outputOwner.version) return rejected(state, "stale-destination-version");
  const cornItems = cornItemIds.map((itemId) => state.items.find((item) => item.itemId === itemId));
  if (cornItems.some((item) => item === undefined)) return rejected(state, "item-missing");
  if (cornItems.some((item) => item!.kind !== 1)) return rejected(state, "wrong-item-kind");
  if (cornItems.some((item) => ownerKey(item!) !== ownerKey(stagedOwner))) return rejected(state, "source-mismatch");
  const occupiedOutput = state.items.filter((item) => ownerKey(item) === ownerKey(outputOwner)).length;
  if (occupiedOutput + eggItemIds.length > outputOwner.capacity) return rejected(state, "insufficient-capacity");
  if (nextVersion(stagedOwner.version) === null || nextVersion(outputOwner.version) === null) {
    return rejected(state, "version-exhausted");
  }

  const cornIdSet = new Set(cornItemIds);
  const occupiedSlots = new Set(
    state.items.filter((item) => ownerKey(item) === ownerKey(outputOwner)).map((item) => item.slotIndex),
  );
  const lineage = cornItems.map((item, index): TendLineagePair => Object.freeze({
    cornItemId: item!.itemId,
    eggItemId: eggItemIds[index]!,
    lineageId: item!.lineageId,
  }));
  const eggs = lineage.map((pair) => {
    let slotIndex = 0;
    while (occupiedSlots.has(slotIndex)) slotIndex += 1;
    occupiedSlots.add(slotIndex);
    return Object.freeze({
      itemId: pair.eggItemId,
      kind: 2,
      lineageId: pair.lineageId,
      ownerKind: OWNER_KIND.PEN_OUTPUT,
      ownerId: outputOwner.ownerId,
      slotIndex,
      version: 0,
    });
  });
  const appended = appendReceiptAndEvent(
    state,
    command,
    EVENT_KIND.ITEM_CREATED,
    "attended-tend-committed",
    [...cornItemIds, ...eggItemIds],
  );
  const nextState = createLedgerState({
    items: [...state.items.filter((item) => !cornIdSet.has(item.itemId)), ...eggs],
    owners: state.owners.map((candidate) => {
      if (ownerKey(candidate) === ownerKey(stagedOwner)) return { ...candidate, version: stagedOwner.version + 1 };
      if (ownerKey(candidate) === ownerKey(outputOwner)) return { ...candidate, version: outputOwner.version + 1 };
      return candidate;
    }),
    actors: state.actors,
    receipts: appended.receipts,
    events: appended.events,
    currency: state.currency,
    navRevision: state.navRevision,
    nextEventSequence: appended.nextEventSequence,
  });
  return Object.freeze({
    committed: true,
    reason: "committed",
    state: nextState,
    lineage: Object.freeze(lineage),
    cashDelta: 0,
  });
}

export function commitEggSaleBatch(
  state: LedgerState,
  command: SaleBatchCommand,
): ManualChainTransactionResult {
  const commonReason = validateCommon(state, command);
  if (commonReason !== null) return rejected(state, commonReason);
  let cashPerEgg: number;
  try {
    cashPerEgg = integer(command.cashPerEgg, "cashPerEgg", 1);
  } catch {
    return rejected(state, "invalid-command");
  }
  const eggItemIds = [...command.eggItemIds].sort((left, right) => left - right);
  if (eggItemIds.length === 0 || new Set(eggItemIds).size !== eggItemIds.length) {
    return rejected(state, "invalid-batch-items");
  }
  const cargoOwner = owner(state, command.cargoOwner);
  const marketOwner = owner(state, command.marketOwner);
  if (cargoOwner === undefined || marketOwner === undefined) return rejected(state, "owner-missing");
  if (cargoOwner.ownerKind !== OWNER_KIND.ACTOR_CARGO || cargoOwner.ownerId !== command.actorId || marketOwner.ownerKind !== OWNER_KIND.MARKET_SERVICE) {
    return rejected(state, "wrong-owner-kind");
  }
  if (cargoOwner.version !== command.cargoOwner.version) return rejected(state, "stale-source-version");
  if (marketOwner.version !== command.marketOwner.version) return rejected(state, "stale-destination-version");
  const eggs = eggItemIds.map((itemId) => state.items.find((item) => item.itemId === itemId));
  if (eggs.some((item) => item === undefined)) return rejected(state, "item-missing");
  if (eggs.some((item) => item!.kind !== 2)) return rejected(state, "wrong-item-kind");
  if (eggs.some((item) => ownerKey(item!) !== ownerKey(cargoOwner))) return rejected(state, "source-mismatch");
  const cashDelta = cashPerEgg * eggItemIds.length;
  if (!Number.isSafeInteger(cashDelta) || !Number.isSafeInteger(state.currency + cashDelta)) {
    return rejected(state, "currency-overflow");
  }
  if (nextVersion(cargoOwner.version) === null || nextVersion(marketOwner.version) === null) {
    return rejected(state, "version-exhausted");
  }
  const appended = appendReceiptAndEvent(
    state,
    command,
    EVENT_KIND.TRANSFER_COMMITTED,
    "egg-sale-committed",
    eggItemIds,
  );
  const eggSet = new Set(eggItemIds);
  const nextState = createLedgerState({
    items: state.items.filter((item) => !eggSet.has(item.itemId)),
    owners: state.owners.map((candidate) => {
      if (ownerKey(candidate) === ownerKey(cargoOwner)) return { ...candidate, version: cargoOwner.version + 1 };
      if (ownerKey(candidate) === ownerKey(marketOwner)) return { ...candidate, version: marketOwner.version + 1 };
      return candidate;
    }),
    actors: state.actors,
    receipts: appended.receipts,
    events: appended.events,
    currency: state.currency + cashDelta,
    navRevision: state.navRevision,
    nextEventSequence: appended.nextEventSequence,
  });
  return Object.freeze({
    committed: true,
    reason: "committed",
    state: nextState,
    lineage: Object.freeze([]),
    cashDelta,
  });
}
